import { compareSsn, formatDate, maskedSsn, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()

const dateValue = (date) => {
    if (!date || typeof date !== 'object' || !date.Year) return 0
    return new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime()
}

const sentence = (value) => (/\d/.test(text(value)) && !/[1-9]/.test(text(value)) ? '' : text(value))

export const SEVERITY = {
    felony: { rank: 4, label: 'Felony', tone: 'declined' },
    misdemeanor: { rank: 3, label: 'Misdemeanor', tone: 'watch' },
    other: { rank: 2, label: 'Other offense', tone: 'neutral' },
    traffic: { rank: 1, label: 'Traffic or infraction', tone: 'neutral' },
}

const severityFrom = (values) => {
    const words = values.map(upper).filter(Boolean)
    if (!words.length) return null
    const all = words.join(' | ')
    const codes = words.filter(w => /^[A-Z0-9]{1,5}$/.test(w))
    if (/FELONY/.test(all) || codes.some(c => /^F[A-Z0-9]{0,4}$/.test(c) && !/^FORF/.test(c))) return 'felony'
    if (/MISD|MISDEMEANOR/.test(all) || codes.some(c => /^(M[A-Z0-9]{0,3}|AM)$/.test(c))) return 'misdemeanor'
    if (/TRAFFIC|INFRACT|PARKING|CITATION|TOLL|ORDINANC|FORFEITURE|MOTORIST|NON-CRIMINAL/.test(all)) return 'traffic'
    return null
}
export const severityOf = (offense, record) => severityFrom(list(offense?.Court?.Level))
    || severityFrom([...list(offense?.Arrest?.Level), ...list(offense?.OffenseType), ...list(offense?.CaseTypeDescription), ...list(offense?.CaseType)])
    || severityFrom(list(record?.CaseTypeDescription)) || 'other'

const OUTCOMES = [
    [/NOT GUILTY|ACQUIT|NOT CONVICT|NO CONVICT|WITHOUT CONVICT/, 'Not guilty', 'neutral'],
    [/VACATE|SET ASIDE|REVERSED|OVERTURN|EXPUNG|PLEA WITHDRAWN/, 'Set aside', 'neutral'],
    [/NOT PROVIDED|NO INFORMATION/, 'Outcome not provided', 'neutral'],
    [/NOLLE|NOL PROS|DROPPED|ABANDON|NOT FILED|NOT FILE|STET|NO ACTION/, 'Dropped', 'neutral'],
    [/DISMISS|STRICKEN/, 'Dismissed', 'neutral'],
    [/WITHHELD|DEFER|DIVERSION/, 'Deferred or withheld', 'watch'],
    [/GUILTY|CONVICT|CONV-|ADJUDICATED/, 'Convicted', 'declined'],
    [/PAID|PREPAID|CIVIL PENALTY|FINE/, 'Paid fine', 'neutral'],
    [/WARRANT|CAPIAS|FAILURE TO APPEAR/, 'Warrant', 'declined'],
    [/PENDING|OPEN|ACTIVE/, 'Pending', 'watch'],
    [/^DISPOSED|STATUS:CLOSED$|^CLOSED/, 'Closed, outcome not stated', 'neutral'],
]
export const outcomeOf = (disposition) => {
    const value = upper(disposition)
    if (!value) return null
    const match = OUTCOMES.find(([pattern]) => pattern.test(value))
    return match ? { label: match[1], tone: match[2] } : { label: 'Other outcome', tone: 'neutral' }
}

const NEGATED_STATUS = /\b(NOT|NO|NON|NEVER)[\s-]+(IN[\s-]+)?(CUSTODY|JAIL|WARRANTS?|WANTED|FUGITIVE|INCARCERATED|ON[\s-]+PAROLE|PAROLE|ON[\s-]+PROBATION|PROBATION(ER)?|SUPERVISED|SUPERVISION|BOOKED)\b/g
const STATUS_WARNINGS = [[/FUGITIVE/, 'Fugitive'], [/IN CUSTODY|^CUSTODY|IN JAIL|BOOKED/, 'In custody'],
    [/PROBATION(ER)?$|ACTIVE PROBATIONER|SUPERVIS/, 'On probation'], [/PAROLE(?!.*INACTIVE)/, 'On parole'], [/WARRANT/, 'Warrant']]

const sameDob = (a, b) => {
    if (!a?.Year || !b?.Year) return null
    if (Number(a.Year) !== Number(b.Year)) return 'different'
    if (a.Month && b.Month && Number(a.Month) !== Number(b.Month)) return 'different'
    if (a.Day && b.Day && Number(a.Day) !== Number(b.Day)) return 'different'
    return a.Month && b.Month && a.Day && b.Day ? 'same' : 'year'
}

const levelLabel = (value) => titleCase(text(value)).replace(/\b(Class|Level|Degree) ([a-z])\b/g, (m, word, letter) => `${word} ${letter.toUpperCase()}`)

const nameCase = (value) => titleCase(value).replace(/(^|\s)([a-z])(?=\s|$)/g, (m, space, letter) => space + letter.toUpperCase())
const personName = (name) => nameCase([name?.First, name?.Middle, name?.Last, name?.Suffix].filter(Boolean).join(' '))
const place = (record) => [titleCase(record?.CountyOfOrigin || record?.Address?.County) && `${titleCase(record?.CountyOfOrigin || record?.Address?.County)} County`.replace(/ County County$/, ' County'),
    titleCase(record?.StateOfOrigin || record?.Address?.State)].filter(Boolean).join(', ')
const money = (value) => {
    const number = Number(text(value).replace(/[^0-9.]/g, ''))
    return number > 0 ? number.toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace(/\.00$/, '') : ''
}

const offenseDate = (o, record) => o?.OffenseDate || o?.Court?.DispositionDate || o?.Arrest?.Date || record?.CaseFilingDate
const offenseName = (o) => titleCase(text(o?.Court?.Offense) && !/^NOT (SPECIFIED|PROVIDED)/i.test(text(o?.Court?.Offense)) ? o.Court.Offense
    : o?.Arrest?.Offense || o?.Court?.Description || '')

export const buildCase = (record, owner = {}, now = new Date()) => {
    const offenses = list(record?.Offenses?.Offense).filter(o => o && typeof o === 'object')
    const items = (offenses.length ? offenses : [{}]).map(o => {
        const severity = severityOf(o, record)
        const outcome = outcomeOf(o?.Court?.Disposition || o?.Arrest?.Disposition)
        const date = offenseDate(o, record)
        return {
            name: offenseName(o) || 'Offense not named',
            severity, outcome, date, dateLabel: formatDate(date),
            jail: sentence(o?.CourtSentence?.Jail), probation: sentence(o?.CourtSentence?.Probation),
            details: [
                ['Court level', levelLabel(o?.Court?.Level)],
                ['Outcome', titleCase(o?.Court?.Disposition)], ['Outcome date', formatDate(o?.Court?.DispositionDate)],
                ['Plea', titleCase(o?.Court?.Plea)], ['Counts', text(o?.NumberCounts || o?.Count)],
                ['Offense date', formatDate(o?.OffenseDate)], ['Offense town', titleCase(o?.OffenseTown)],
                ['Statute', text(o?.Court?.Statute || o?.Arrest?.Statute)],
                ['Fine', money(o?.Court?.Fine)], ['Court costs', money(o?.Court?.Costs)],
                ['Jail', titleCase(sentence(o?.CourtSentence?.Jail))], ['Probation', titleCase(sentence(o?.CourtSentence?.Probation))],
                ['Suspended', titleCase(sentence(o?.CourtSentence?.Suspended))],
                ['Sentence', titleCase(o?.Sentence || o?.SentenceLengthDescription)], ['Sentence date', formatDate(o?.SentenceDate)],
                ['Maximum term', titleCase(o?.MaximumTerm)], ['Minimum term', titleCase(o?.MinimumTerm)],
                ['Incarceration date', formatDate(o?.IncarcerationDate)],
                ['Arrest', [titleCase(o?.Arrest?.Agency), formatDate(o?.Arrest?.Date), titleCase(o?.Arrest?.Offense),
                    o?.Arrest?.Level && `level ${upper(o.Arrest.Level)}`, titleCase(o?.Arrest?.Type)].filter(Boolean).join(' · ')],
                ['Arrest outcome', titleCase(o?.Arrest?.Disposition)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    })
    const worst = [...items].sort((a, b) => SEVERITY[b.severity].rank - SEVERITY[a.severity].rank || dateValue(b.date) - dateValue(a.date))[0]
    const severity = worst.severity
    const latestDate = [...items.map(i => i.date), record?.CaseFilingDate].filter(d => dateValue(d)).sort((a, b) => dateValue(b) - dateValue(a))[0]
    const latest = dateValue(latestDate)
    const convicted = items.some(i => i.outcome?.label === 'Convicted')
    const prison = list(record?.PrisonSentences?.PrisonSentence).filter(Boolean)
    const parole = list(record?.ParoleSentences?.ParoleSentence).filter(Boolean)
    const jail = items.some(i => i.jail) || offenses.some(o => dateValue(o?.IncarcerationDate))
    const probation = items.some(i => i.probation)
    const status = upper(record?.Status).replace(NEGATED_STATUS, ' ')
    const statusWarning = STATUS_WARNINGS.find(([pattern]) => pattern.test(status) && !/INACTIVE|DISCHARG|RELEASED/.test(status))?.[1] || ''
    const dob = sameDob(record?.DOB, owner?.DOB)
    const ssn = compareSsn(record?.SSN, owner?.SSN)
    const outcomeChip = convicted ? { label: 'Convicted', tone: 'declined' } : worst.outcome
    const recentYears = latest ? (now.getTime() - latest) / (365.25 * 86400000) : null

    return {
        more: moreFromLexis(record, 'CriminalRecords/Criminal[]'),
        severity, rank: SEVERITY[severity].rank, latest, recent: recentYears != null && recentYears <= 10,
        title: [worst.name, items.length > 1 && `+ ${items.length - 1} more`].filter(Boolean).join(' '),
        facts: [formatDate(latestDate), place(record),
            titleCase(record?.CaseTypeDescription)].filter(Boolean).join(' · '),
        chips: [
            { label: SEVERITY[severity].label, tone: SEVERITY[severity].tone },
            outcomeChip && { label: outcomeChip.label, tone: outcomeChip.tone },
            prison.length ? { label: 'Prison', tone: 'declined' } : jail ? { label: 'Jail', tone: 'declined' } : null,
            probation && !prison.length && { label: 'Probation', tone: 'watch' },
            parole.length > 0 && { label: 'Parole', tone: 'watch' },
            statusWarning && !(statusWarning === 'On probation' && probation) && !(statusWarning === 'On parole' && parole.length)
                && { label: statusWarning, tone: ['Fugitive', 'In custody', 'Warrant'].includes(statusWarning) ? 'declined' : 'watch' },
            dob === 'different' && { label: 'Date of birth differs', tone: 'watch' },
            dob !== 'different' && ssn === 'different' && { label: 'SSN differs', tone: 'watch' },
        ].filter(Boolean),
        details: [
            ['Same person?', [dob === 'same' ? 'Date of birth matches the owner' : dob === 'year' ? 'Birth year matches the owner (no full date to compare)' : dob === 'different' ? 'Date of birth differs from the owner' : '',
                ssn === 'same' ? 'SSN matches the owner' : ssn === 'prefix' ? "SSN starts with the same 5 digits as the owner's" : ssn === 'different' ? 'SSN differs from the owner' : ''].filter(Boolean).join(' · ')
                || 'Lexis matched this record by name; no date of birth or SSN to compare'],
            ['Name on record', personName(record?.Name)],
            ['Date of birth on record', formatDate(record?.DOB)],
            ['SSN on record', maskedSsn(record?.SSN)],
            ['Case number', text(record?.CaseNumber || offenses.map(o => o?.Court?.CaseNumber).find(Boolean))],
            ['Case type', titleCase(record?.CaseTypeDescription)],
            ['Case filed', formatDate(record?.CaseFilingDate)],
            ['Source', titleCase(record?.DataSource)],
            ['Court', titleCase(offenses.map(o => o?.Court?.Description).find(Boolean))],
            ['Where', place(record)],
            ['Record status', titleCase(record?.Status)],
            ['Department of Corrections number', text(record?.DOCNumber)],
            ['Prison', prison.map(p => [titleCase(p?.Location), p?.AdmittedDate && `admitted ${formatDate(p.AdmittedDate)}`,
                p?.ScheduledReleaseDate && `release ${formatDate(p.ScheduledReleaseDate)}`, titleCase(p?.CurrentStatus), titleCase(p?.CustodyType),
                titleCase(p?.Sentence)].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
            ['Parole', parole.map(p => [p?.StartDate && `from ${formatDate(p.StartDate)}`, p?.ActualEndDate && `to ${formatDate(p.ActualEndDate)}`,
                titleCase(p?.CurrentStatus), titleCase(p?.County) && `${titleCase(p.County)} County`, titleCase(p?.Length)].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        blocks: items.map((i, n) => ({
            title: `${items.length > 1 ? `Offense ${n + 1}: ` : ''}${i.name} · ${SEVERITY[i.severity].label}`,
            details: i.details,
        })),
    }
}

export function buildCriminalRecords(records, owner = {}, now = new Date()) {
    const cases = list(records).filter(r => r && typeof r === 'object').map(r => buildCase(r, owner, now))
        .sort((a, b) => b.rank - a.rank || b.latest - a.latest)
    const serious = cases.filter(c => c.severity !== 'traffic')
    const traffic = cases.filter(c => c.severity === 'traffic')
    const count = (severity) => cases.filter(c => c.severity === severity)
    const latestYear = (rows) => (rows.length ? new Date(Math.max(...rows.map(c => c.latest))).getFullYear() : null)
    const felonies = count('felony')
    const misdemeanors = count('misdemeanor')
    return {
        cases, serious, traffic,
        summary: {
            felonies: felonies.length, felonyLatest: latestYear(felonies.filter(c => c.latest)),
            recentFelonies: felonies.filter(c => c.recent).length,
            misdemeanors: misdemeanors.length, misdemeanorLatest: latestYear(misdemeanors.filter(c => c.latest)),
            other: count('other').length, traffic: traffic.length,
            convicted: cases.filter(c => c.chips.some(ch => ch.label === 'Convicted')).length,
            warnings: cases.filter(c => c.chips.some(ch => ['Fugitive', 'In custody', 'Warrant'].includes(ch.label))).length,
        },
    }
}
